import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Screen, ScreenTitle, PrimaryButton, ErrorNote } from '../ui/Screen.jsx'
import { FloatField } from '../ui/Field.jsx'
import { login } from '../auth.js'
import { useConfigBundle, screensOf } from '../config.js'
import { NavWordmark } from '../ui/NavBar.jsx'

/**
 * Sign in. Cookie-based, so this screen never sees or stores a token.
 *
 * `?next=` is set by the global 401 handler, so a participant bumped out
 * mid-battery lands back where they were rather than at the start.
 *
 * THREE OUTCOMES, NOT TWO: signed in, refused, and ENDED.
 *
 * The third is the one that is easy to miss and expensive to get wrong. A
 * withdrawn participant who closes the tab and comes back has no session and no
 * router state, so without this she meets a sign-in form that rejects her —
 * indistinguishable from a wrong password. She would be left believing her
 * account is broken rather than knowing she left, on the app she deliberately
 * left, with no way to find out which.
 *
 * THE ORDER IS A SECURITY PROPERTY, NOT A CONVENIENCE. The ending is revealed
 * ONLY after email AND password verify. The server enforces this: it compares
 * against a dummy hash for an unknown email, returns one identical 401 for
 * "unknown email" and "wrong password", and only then checks withdrawn status —
 * with its own comment noting the credentials were right, so it is not counted
 * as a failed guess.
 *
 * Why it matters more here than for the 401: critic proved registration was a
 * membership oracle, confirming enrolment in a suicidality study from an email
 * alone. Revealing withdrawal on an email alone would be strictly worse — it
 * confirms enrolment AND that she left. This client must never widen that:
 * never surface "ended" on anything but a 403, and never distinguish the two
 * 401 causes even if a future response tempts it.
 */
export default function Login() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next')
  /*
   * GET /api/config needs no session, so this is available here and stays
   * available to a withdrawn participant. That is what lets the ending carry
   * config's governed wording on the cold path — she arrives with no router
   * state, and the alternative was an inlined literal on the last screen she
   * can ever reach.
   */
  const { data: bundle } = useConfigBundle()

  const [form, setForm] = useState({ email: '', password: '' })
  const [failure, setFailure] = useState(null)
  const [busy, setBusy] = useState(false)

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  async function submit(e) {
    e.preventDefault()
    setFailure(null)
    setBusy(true)
    try {
      await login({ email: form.email.trim(), password: form.password })
      navigate(safeNext(next), { replace: true })
    } catch (err) {
      /*
       * ENDED, not refused. 403 here means the credentials were CORRECT and the
       * account is withdrawn — the server checks status only after verifying
       * both, so this branch cannot be reached by guessing an email.
       *
       * She is not locked out by mistake; she left. So she gets the ending
       * rather than a sign-in error telling her that her own account is a
       * problem. `?next=` is deliberately ignored on this path: there is
       * nothing to resume, and honouring it would bounce her at a route that
       * 401s straight back to here.
       *
       * The copy travels with her. config owns these words and the ending must
       * render without a session, so they are read here — where the bundle is
       * reachable — rather than fetched there. `data_retained` comes from the
       * response when the server sends one; absent, the screen states retention
       * anyway, because retention is a property of withdrawal itself.
       */
      if (err.status === 403) {
        navigate('/withdrawn', {
          replace: true,
          state: {
            dataRetained: err.body?.data_retained,
            copy: screensOf(bundle)?.withdrawal ?? null,
          },
        })
        return
      }
      /*
       * ONE REFUSAL, ONE MESSAGE, AND IT IS OURS.
       *
       * The server returns an identical 401 for an unknown email and a wrong
       * password — deliberately, because telling them apart is the membership
       * oracle. This screen must never widen that, so the two cases are not
       * distinguished here either and never should be.
       *
       * The sentence is the client's rather than the server's: participant
       * words do not belong in Python, and a raw backend string on a
       * participant screen is the rule DS-2 §3.10 sets. It also states what she
       * can do, which "email or password is incorrect" does not.
       *
       * Ungoverned copy, flagged: config owns screen wording now (CFG-30) and
       * screens.json has no key for a sign-in refusal. Mine until it does.
       */
      if (err.status === 401) {
        setFailure(new Error('That email address and password don’t match. Please try again.'))
        return
      }
      setFailure(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen nav={{ left: <NavWordmark /> }} action={<PrimaryButton type="submit" form="login" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</PrimaryButton>}>
      <ScreenTitle>Sign in</ScreenTitle>

      <form id="login" onSubmit={submit} noValidate>
        <FloatField
          id="email"
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          value={form.email}
          onChange={set('email')}
        />

        <FloatField
          id="password"
          label="Password"
          type="password"
          autoComplete="current-password"
          value={form.password}
          onChange={set('password')}
        />

        <ErrorNote error={failure} />

      </form>

      <p className="mt-6 text-center text-sm text-[var(--ink-700)]">
        New here?{' '}
        <Link to="/register" className="inline-flex min-h-[max(44px,2.75rem)] items-center px-[var(--s-2)] font-semibold text-[var(--accent)] underline">
          Create an account
        </Link>
      </p>
    </Screen>
  )
}

/**
 * Only ever redirect inside this app. An open redirect on a login screen is
 * how a phishing link gets to look legitimate.
 */
export function safeNext(next) {
  if (typeof next !== 'string' || !next.startsWith('/')) return '/'
  if (next.startsWith('//')) return '/'
  if (next.startsWith('/login') || next.startsWith('/register')) return '/'
  return next
}
